import { api } from '../api.ts';
import type { PaymentMode, PaymentSync } from './types.ts';

/**
 * Everything about a payment that does not need Stripe's own script. Pages import from here, so
 * loading a page never pulls Stripe in; only the payment form itself does (see StripeElements).
 */

/** Order statuses that mean the payment has not been settled yet. */
export const PAYMENT_PENDING = new Set(['DRAFT', 'AWAITING_PAYMENT']);

/**
 * Shown when the secure payment form cannot be loaded at all. It does not tell the customer to
 * reload: on the payment step a reload loses the order they are paying for, so the screens offer a
 * Try again instead.
 */
export const PAYMENT_LOAD_ERROR =
  'Secure payment did not load. Check your connection and try again.';

export function isPaidStatus(status: string): boolean {
  return !PAYMENT_PENDING.has(status) && status !== 'PAYMENT_FAILED' && status !== 'CANCELED';
}

/** One sync-payment call. Never throws: a failed call returns null so callers can keep going. */
export async function syncPaymentOnce(orderId: string): Promise<PaymentSync | null> {
  try {
    return (await api.post<PaymentSync>(`/orders/${orderId}/sync-payment`)).data;
  } catch {
    return null;
  }
}

/**
 * Ask the API to read the payment from the provider until the order leaves AWAITING_PAYMENT/DRAFT.
 * Returns the last answer, or null when every call failed. Never throws.
 */
export async function syncPaymentUntilSettled(
  orderId: string,
  opts: { attempts?: number; intervalMs?: number; isCancelled?: () => boolean } = {},
): Promise<PaymentSync | null> {
  const attempts = opts.attempts ?? 8;
  const intervalMs = opts.intervalMs ?? 1000;
  let last: PaymentSync | null = null;
  for (let i = 0; i < attempts; i++) {
    if (opts.isCancelled?.()) return last;
    const r = await syncPaymentOnce(orderId);
    if (r) {
      last = r;
      if (!PAYMENT_PENDING.has(r.status)) return r;
    }
    if (i < attempts - 1) await new Promise((res) => setTimeout(res, intervalMs));
  }
  return last;
}

/**
 * Saving a card on its own, with no order behind it: the account page (#67). There is nothing to
 * sync afterwards, so it says when the card is saved instead.
 */
export interface SaveCardProps {
  purpose: 'save_card';
  email: string | null;
  /** Where Stripe sends the customer back after a redirect (3-D Secure, wallets). */
  returnUrl: string;
  onSaved: () => void;
}

/** Either paying for an order, or just putting a card on file. */
export type PaymentFormProps = PaymentProps | SaveCardProps;

export const isSaveCard = (p: PaymentFormProps): p is SaveCardProps => 'purpose' in p;

export interface PaymentProps {
  orderId: string;
  mode: PaymentMode;
  amountCents: number;
  email: string | null;
  /** Where Stripe sends the customer back after a redirect (3-D Secure, wallets). */
  returnUrl: string;
  /**
   * Called after the provider accepted the payment and the sync poll finished. `result` is null
   * when the poll could not reach the API; the order may still be confirmed by webhook.
   */
  onSettled: (result: PaymentSync | null) => void;
}
